export function initRegistration(): void {
  const card = document.querySelector<HTMLElement>("[data-reg-init]");
  if (!card || card.hasAttribute("data-reg-inited")) return;
  card.setAttribute("data-reg-inited", "");

  initPasswordToggle();
  initCharacterCounters();
  initCompanySuggestionsDropdown();
  initReferralAttribution();
  initFormValidation();
}

// ── Referral code attribution (URL ?ref= / localStorage) & preview ────────────

const REFERRAL_STORAGE_KEY = "takt_ref";
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredReferral {
  code: string;
  ts: number;
}

function readStoredReferral(): string | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredReferral>;
    if (typeof parsed.code !== "string" || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > REFERRAL_TTL_MS) return null;
    return parsed.code || null;
  } catch {
    return null;
  }
}

function storeReferral(code: string): void {
  try {
    const payload: StoredReferral = { code, ts: Date.now() };
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage недоступен — атрибуция работает только в рамках текущего URL.
  }
}

function initReferralAttribution(): void {
  const hiddenInput = document.getElementById("reg-referral-code") as HTMLInputElement | null;
  const visibleInput = document.getElementById(
    "reg-referral-code-visible",
  ) as HTMLInputElement | null;
  const checkBtn = document.getElementById("reg-referral-check-btn") as HTMLButtonElement | null;
  const previewContainer = document.getElementById("reg-referral-preview");

  if (!hiddenInput) return;

  const urlCode = new URLSearchParams(window.location.search).get("ref");
  const attributedCode = (urlCode ?? "").trim();

  if (attributedCode) {
    storeReferral(attributedCode);
    if (!hiddenInput.value) hiddenInput.value = attributedCode;
    if (visibleInput && !visibleInput.value) visibleInput.value = attributedCode;
  } else if (!hiddenInput.value) {
    const stored = readStoredReferral();
    if (stored) {
      hiddenInput.value = stored;
      if (visibleInput && !visibleInput.value) visibleInput.value = stored;
    }
  }

  if (!visibleInput) return;

  // Ручной ввод приоритетнее сохранённого кода: синхронизируем скрытое поле.
  const syncHidden = (): void => {
    hiddenInput.value = visibleInput.value.trim();
    if (checkBtn) {
      const empty = hiddenInput.value.length === 0;
      checkBtn.disabled = empty;
      checkBtn.setAttribute("aria-disabled", empty ? "true" : "false");
    }
    if (previewContainer) previewContainer.innerHTML = "";
  };
  visibleInput.addEventListener("input", syncHidden);
  syncHidden();

  if (checkBtn && previewContainer) {
    checkBtn.addEventListener("click", () => {
      const code = hiddenInput.value.trim();
      if (!code || !window.htmx) return;
      window.htmx.ajax("GET", `/auth/referrer-preview?code=${encodeURIComponent(code)}`, {
        target: "#reg-referral-preview",
        swap: "innerHTML",
      });
    });
  }
}

function updateCharacterCounter(input: HTMLInputElement | HTMLTextAreaElement): void {
  const counterId = input.getAttribute("data-char-counter-id");
  if (!counterId) return;

  const counter = document.getElementById(counterId);
  if (!counter) return;

  const maxLengthAttr = input.getAttribute("maxlength");
  const maxLength = maxLengthAttr ? Number.parseInt(maxLengthAttr, 10) : input.value.length;
  const safeMaxLength = Number.isFinite(maxLength) && maxLength >= 0 ? maxLength : input.value.length;

  counter.textContent = `${input.value.length}/${safeMaxLength}`;
}

function initCharacterCounters(): void {
  const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    ".auth__card--register [data-char-counter-id]",
  );

  inputs.forEach((input) => {
    updateCharacterCounter(input);
    input.addEventListener("input", () => {
      updateCharacterCounter(input);
    });
  });
}

function clampToMaxLength(input: HTMLInputElement | HTMLTextAreaElement): void {
  const maxLengthAttr = input.getAttribute("maxlength");
  if (!maxLengthAttr) return;

  const maxLength = Number.parseInt(maxLengthAttr, 10);
  if (!Number.isFinite(maxLength) || maxLength < 0) return;

  if (input.value.length > maxLength) {
    input.value = input.value.slice(0, maxLength);
  }
}

function initPasswordToggle(): void {
  const btn = document.querySelector<HTMLButtonElement>(
    ".auth__card--register .auth-field__pwd-toggle",
  );
  if (!btn) return;

  const input = document.getElementById("reg-password") as HTMLInputElement | null;
  const iconShow = btn.querySelector<SVGElement>(".auth-field__pwd-icon--show");
  const iconHide = btn.querySelector<SVGElement>(".auth-field__pwd-icon--hide");

  if (!input || !iconShow || !iconHide) return;

  btn.addEventListener("click", () => {
    const revealing = input.type === "password";
    input.type = revealing ? "text" : "password";
    iconShow.style.display = revealing ? "none" : "";
    iconHide.style.display = revealing ? "" : "none";
    btn.setAttribute("aria-label", revealing ? "Скрыть пароль" : "Показать / скрыть пароль");
  });
}

function initCompanySuggestionsDropdown(): void {
  const companyNameInput = document.getElementById("reg-company") as HTMLInputElement | null;
  const companyInnInput = document.getElementById("reg-company-inn") as HTMLInputElement | null;
  const suggestionsContainer = document.getElementById("company-suggestions");

  if (!companyNameInput || !companyInnInput || !suggestionsContainer) return;

  suggestionsContainer.addEventListener("click", (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>(".company-suggestions__item");
    if (!item) return;
    companyNameInput.value = item.dataset.name ?? "";
    clampToMaxLength(companyNameInput);
    updateCharacterCounter(companyNameInput);
    companyInnInput.value = item.dataset.inn ?? "";
    suggestionsContainer.innerHTML = "";
  });

  suggestionsContainer.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const item = (e.target as HTMLElement).closest<HTMLElement>(".company-suggestions__item");
    if (!item) return;
    e.preventDefault();
    companyNameInput.value = item.dataset.name ?? "";
    clampToMaxLength(companyNameInput);
    updateCharacterCounter(companyNameInput);
    companyInnInput.value = item.dataset.inn ?? "";
    suggestionsContainer.innerHTML = "";
  });

  document.addEventListener("click", (e) => {
    if (!suggestionsContainer.contains(e.target as Node) && e.target !== companyNameInput) {
      suggestionsContainer.innerHTML = "";
    }
  });

  companyNameInput.addEventListener("input", () => {
    companyInnInput.value = "";
  });
}

// ── Form validation & submit button state ────────────────────────────────────

function initFormValidation(): void {
  const form = document.querySelector<HTMLFormElement>(".auth__card--register .auth__form");
  if (!form) return;

  const submitBtn = document.getElementById("reg-submit-btn") as HTMLButtonElement | null;

  const textInputs = [
    form.querySelector<HTMLInputElement>("[name='fullname']"),
    form.querySelector<HTMLInputElement>("[name='email']"),
    form.querySelector<HTMLInputElement>("[name='contacts']"),
    form.querySelector<HTMLInputElement>("[name='password']"),
    form.querySelector<HTMLInputElement>("[name='company_name']"),
  ];
  const checkboxes = [
    form.querySelector<HTMLInputElement>("[name='agree_legal']"),
  ];
  const maxLengthInputs = Array.from(
    form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input[maxlength], textarea[maxlength]"),
  );

  function isFormValid(): boolean {
    const allFilled = textInputs.every((inp) => (inp?.value ?? "").trim().length > 0);
    const allChecked = checkboxes.every((cb) => cb?.checked === true);
    return allFilled && allChecked;
  }

  function syncButtonState(): void {
    if (!submitBtn) return;
    const valid = isFormValid();
    submitBtn.setAttribute("aria-disabled", valid ? "false" : "true");
    submitBtn.classList.toggle("auth__submit--inactive", !valid);
  }

  textInputs.forEach((inp) => {
    inp?.addEventListener("input", () => {
      clampToMaxLength(inp);
      updateCharacterCounter(inp);
      inp.classList.remove("auth-field__input--error");
      syncButtonState();
    });
  });
  checkboxes.forEach((cb) => {
    cb?.addEventListener("change", syncButtonState);
  });

  syncButtonState();

  form.addEventListener("submit", (e) => {
    maxLengthInputs.forEach((input) => {
      clampToMaxLength(input);
      updateCharacterCounter(input);
    });
    if (!isFormValid()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      highlightErrors(textInputs);
      shakeButton(submitBtn);
    }
  });
}

function highlightErrors(inputs: (HTMLInputElement | HTMLTextAreaElement | null)[]): void {
  inputs.forEach((inp) => {
    if (inp && (inp.value ?? "").trim().length === 0) {
      inp.classList.add("auth-field__input--error");
    }
  });
}

function shakeButton(btn: HTMLButtonElement | null): void {
  if (!btn) return;
  btn.classList.remove("auth__submit--shake");
  // Force reflow so the animation restarts even if it was already running
  void btn.offsetWidth;
  btn.classList.add("auth__submit--shake");
  btn.addEventListener("animationend", () => btn.classList.remove("auth__submit--shake"), {
    once: true,
  });
}
